/**
 * FoundingLightPricing — Soulumo Founding member pricing hero.
 *
 * Founding member: £6.99/month (was £9.99) or £60.99/year — the own-voice plan.
 */
import React from "react";

const TOK = {
  navy: "#0a1428",
  navyDeep: "#050b1a",
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.42)",
  creamGhost: "rgba(245,236,217,0.28)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.86)",
  goldMist: "rgba(224,169,122,0.22)",
};

const OFFER = {
  monthly:    "£6.99",   // Founding member monthly
  wasMonthly: "£9.99",   // struck-through
  annual:     "£60.99",
};

const SaveBadge = () => (
  <span className="rounded-full px-2.5 py-1 text-[0.58rem] uppercase font-bold whitespace-nowrap" data-testid="founding-annual-badge"
    style={{ background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)", color: "#1a0f05", fontFamily: '"Manrope", sans-serif', letterSpacing: "0.18em" }}>
    Over 3 months free
  </span>
);

export default function FoundingLightPricing({ isAuthed, onClaim }) {
  return (
    <div
      className="relative rounded-3xl px-6 sm:px-10 py-10 sm:py-12 text-center overflow-hidden"
      style={{
        background: "linear-gradient(180deg, rgba(224,169,122,0.09) 0%, rgba(10,20,40,0.85) 55%, rgba(5,11,26,0.95) 100%)",
        border: `1px solid ${TOK.gold}`,
        boxShadow: "0 30px 90px rgba(0,0,0,0.5), 0 0 60px rgba(224,169,122,0.15)",
      }}
      data-testid="founding-light-pricing-card"
    >
      {/* Ambient inner glow at the top */}
      <div
        aria-hidden
        className="absolute pointer-events-none inset-x-0 top-0 h-64"
        style={{
          background: "radial-gradient(circle at 50% 15%, rgba(224,169,122,0.22) 0%, transparent 60%)",
        }}
      />

      <div className="relative">
        {/* Eyebrow */}
        <div
          className="text-[0.6rem] uppercase"
          style={{
            color: TOK.goldSoft,
            fontFamily: '"Manrope", sans-serif',
            letterSpacing: "0.44em",
          }}
        >
          ✦  Soulumo Founding Membership  ✦
        </div>

        {/* Founding member monthly + annual */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 text-left" data-testid="founding-plans">
          <div className="rounded-2xl p-6" style={{ border: `1px solid ${TOK.gold}`, background: "rgba(224,169,122,0.08)" }} data-testid="founding-monthly">
            <div className="text-[0.6rem] uppercase" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', letterSpacing: "0.32em" }}>Founding member · Monthly</div>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1" data-testid="founding-monthly-price">
              <s style={{ fontFamily: '"Manrope", sans-serif', fontSize: "1.05rem", color: TOK.creamMist }}>{OFFER.wasMonthly}</s>
              <span style={{ fontFamily: '"Manrope", sans-serif', fontSize: "clamp(2.5rem, 9vw, 2.9rem)", lineHeight: 0.95, color: TOK.cream, fontWeight: 300 }}>{OFFER.monthly}</span>
              <span style={{ fontFamily: '"Manrope", sans-serif', fontSize: "0.75rem", color: TOK.creamMist, letterSpacing: "0.2em", textTransform: "uppercase" }}>/ month</span>
            </div>
            <p className="mt-2 text-sm" style={{ fontFamily: '"Manrope", sans-serif', color: TOK.goldSoft }} data-testid="founding-monthly-coffee">About the price of one fancy high-street coffee, for a whole month.</p>
            <p className="mt-1 italic" style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim }}>Founding member price — kept for as long as you stay.</p>
          </div>
          <div className="rounded-2xl p-6" style={{ border: `1px solid ${TOK.goldMist}` }} data-testid="founding-annual">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[0.6rem] uppercase" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', letterSpacing: "0.32em" }}>Annual</div>
              <SaveBadge />
            </div>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1" data-testid="founding-annual-price">
              <span style={{ fontFamily: '"Manrope", sans-serif', fontSize: "clamp(2.5rem, 9vw, 2.9rem)", lineHeight: 0.95, color: TOK.cream, fontWeight: 300 }}>{OFFER.annual}</span>
              <span style={{ fontFamily: '"Manrope", sans-serif', fontSize: "0.75rem", color: TOK.creamMist, letterSpacing: "0.2em", textTransform: "uppercase" }}>/ year</span>
            </div>
            <p className="mt-2 italic" style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim }}>One payment for the whole year.</p>
          </div>
        </div>

        {/* Supporting copy */}
        <p
          className="mt-9 italic max-w-md mx-auto"
          style={{
            fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
            fontSize: "clamp(1rem, 3.2vw, 1.1rem)",
            color: TOK.creamDim,
            lineHeight: 1.55,
          }}
          data-testid="founding-light-support-copy"
        >
          Clone your voice once. Every installation then speaks your
          words in your own voice — up to 10 new installations a month.
        </p>

        {/* Primary CTA */}
        <button
          onClick={onClaim}
          data-testid="founding-light-cta"
          className="mt-9 rounded-full px-8 py-4 transition-all"
          style={{
            background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
            border: "1px solid #f5e0c5",
            color: "#1a0f05",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.78rem",
            fontWeight: 700,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            boxShadow: "0 0 44px rgba(224,169,122,0.4), 0 12px 30px rgba(0,0,0,0.35)",
            width: "min(360px, 100%)",
            maxWidth: "100%",
            cursor: "pointer",
          }}
        >
          Start my membership
        </button>

        {/* Reassurance line */}
        <p
          className="mt-4 italic text-[0.82rem]"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            color: TOK.creamMist,
          }}
          data-testid="founding-light-reassure"
        >
          £6.99 / month or £60.99 / year. Cancel any time.
        </p>

        {!isAuthed && (
          <p
            className="mt-2 text-[0.7rem]"
            style={{ color: TOK.creamGhost, fontFamily: '"Manrope", sans-serif', letterSpacing: "0.14em" }}
          >
            Existing member?{" "}
            <a href="/login" className="underline" style={{ color: TOK.goldSoft }}>
              Sign in
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
